import { useQuery } from '@tanstack/react-query'
import { useMeetingTypeHeader } from '@chilipiper/api-queries/src/meeting-type'
import { useApiClient } from '@chilipiper/api-client/src/context'
import type { PublicMeetingType } from '@chilipiper/api-client/src/meeting-types'

const retryWithout404 = (failureCount: number, error: unknown): boolean => {
  if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
    return false
  }
  return failureCount < 3
}

/**
 * Core resolution logic for meeting type ID that may actually be a scheduling link ID.
 *
 * This handles the case where the backend sometimes sends scheduling link IDs instead of meeting
 * type IDs. The hook implements a 3-step resolution:
 *
 * 1. Try to fetch as meeting type
 * 2. If not found, try to fetch as scheduling link
 * 3. Extract the actual meeting type ID from the scheduling link
 */
const useMeetingTypeResolutionCore = (tenantId: string, potentialMeetingTypeId: string) => {
  const { teamScheduling } = useApiClient()

  const {
    data: meetingType,
    isLoading: isLoadingMeetingType,
    error: meetingTypeError,
  } = useMeetingTypeHeader(tenantId, potentialMeetingTypeId, {
    retry: false,
  })

  const shouldTryAsSchedulingLink =
    (!meetingType && !isLoadingMeetingType) || (!!meetingTypeError && !isLoadingMeetingType)

  const { data: schedulingLink, isLoading: isLoadingSchedulingLink } = useQuery({
    queryKey: ['findSchedulingLinkByLinkId', tenantId, potentialMeetingTypeId],
    queryFn: () =>
      teamScheduling.findAAdminSchedulingLinkByAdminSchedulingLinkId(
        tenantId,
        potentialMeetingTypeId
      ),
    enabled: shouldTryAsSchedulingLink,
    retry: retryWithout404,
  })

  const resolvedMeetingTypeId = schedulingLink?.meetingTypeIds?.[0]
  const { data: resolvedMeetingType, isLoading: isLoadingResolvedMeetingType } =
    useMeetingTypeHeader(tenantId, resolvedMeetingTypeId ?? '', {
      enabled: !!resolvedMeetingTypeId,
      retry: false,
    })

  const finalMeetingType = meetingType ?? resolvedMeetingType
  const finalMeetingTypeId = finalMeetingType?.id ?? potentialMeetingTypeId

  const isLoading =
    isLoadingMeetingType ||
    (shouldTryAsSchedulingLink && (isLoadingSchedulingLink || isLoadingResolvedMeetingType))

  return {
    meetingType: finalMeetingType,
    meetingTypeId: finalMeetingTypeId,
    isLoading,
  }
}

export const useMeetingTypeResolution = (tenantId: string, potentialMeetingTypeId: string) => {
  return useMeetingTypeResolutionCore(tenantId, potentialMeetingTypeId)
}

/**
 * Hook for SuggestedTimes component that includes integration with getMeetingTypeId helper. This
 * handles additional logic for RoundRobinSchedulingLink cases.
 *
 * Uses the same core resolution logic but ensures type compatibility for SuggestedTimes.
 *
 * @param tenantId - The tenant ID for the request
 * @param originalMeetingTypeId - The original meeting type ID that could be either a meeting type
 *   ID or scheduling link ID
 * @returns Object with resolved meeting type, ID, and loading state
 */
export const useSuggestedTimesMeetingTypeResolution = (
  tenantId: string,
  originalMeetingTypeId: string
): {
  meetingType: PublicMeetingType | undefined
  meetingTypeId: string
  isLoading: boolean
} => {
  const result = useMeetingTypeResolutionCore(tenantId, originalMeetingTypeId)

  return {
    meetingType: result.meetingType ?? undefined,
    meetingTypeId: result.meetingTypeId,
    isLoading: result.isLoading,
  }
}
